import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { EmployeeSelect, LeaveTypeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { FormGrid } from '@/components/ui/FormGrid';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { HrLeaveRequest } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrLeavesPage() {
  const pagination = usePagination();
  const { getLabel } = useEmployeeOptions();
  const [data, setData] = useState<HrLeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState('1');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [approverId, setApproverId] = useState('1');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.listLeaves({ page: pagination.page, size: pagination.size });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load leaves');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const createLeave = async () => {
    if (!employeeId.trim() || !startDate || !endDate) {
      notify.error('Employee and dates are required');
      return;
    }
    setCreating(true);
    try {
      await hrApi.createLeave({
        employeeId: Number(employeeId),
        leaveTypeId: Number(leaveTypeId) || 1,
        startDate,
        endDate,
        reason: reason.trim() || undefined,
      });
      notify.success('Leave request created');
      setEmployeeId('');
      setReason('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create leave');
    } finally {
      setCreating(false);
    }
  };

  const approveLeave = async (id: number) => {
    const approver = Number(approverId) || 1;
    try {
      await hrApi.approveLeave(id, approver);
      notify.success('Leave approved');
      load();
    } catch (err) {
      showApiError(err, 'Leave approval failed');
    }
  };

  const columns: Column<HrLeaveRequest>[] = [
    { key: 'id', header: '#' },
    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },
    { key: 'startDate', header: 'From' },
    { key: 'endDate', header: 'To' },
    { key: 'totalDays', header: 'Days' },
    { key: 'statusCode', header: 'Status' },
    {
      key: 'id',
      header: 'Actions',
      render: (row) =>
        row.statusCode === 'PENDING' || row.statusCode === 'SUBMITTED' ? (
          <Button variant="secondary" onClick={() => approveLeave(row.id)}>
            Approve
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leave management"
        description="Configurable leave types, balances, and approval workflows"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Leaves' }]}
      />
      <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <h3 className="mb-2 text-sm font-medium">New leave request</h3>
        <FormGrid columns={4} className="items-end">
          <EmployeeSelect value={employeeId} onChange={setEmployeeId} />
          <LeaveTypeSelect value={leaveTypeId} onChange={setLeaveTypeId} />
          <DateRangeFields from={startDate} to={endDate} onFromChange={setStartDate} onToChange={setEndDate} fromLabel="Start" toLabel="End" />
          <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button loading={creating} onClick={() => void createLeave()}>
            Create leave
          </Button>
        </FormGrid>
        <FormGrid columns={4} className="mt-2 items-end">
          <EmployeeSelect
            label="Approver (for approve action)"
            value={approverId}
            onChange={setApproverId}
            className="max-w-[160px]"
          />
        </FormGrid>
      </div>
      <DataTable columns={columns} data={data} loading={loading} keyField="id" {...dataTablePageProps(pagination)} />
    </div>
  );
}
